import React, { useState } from 'react';
import { Loader2, Mail, Shield, AlertTriangle, CheckCircle } from 'lucide-react';
import styles from './index.module.scss';

const AccountDeletion = () => {
  const [step, setStep] = useState(1); // 1: email, 2: OTP, 3: confirmation
  const [formData, setFormData] = useState({
    email: '',
    otp: '',
    reason: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email.trim()) {
      setErrors({ email: 'Email is required' });
      return;
    }
    
    if (!validateEmail(formData.email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Simulate API call to send OTP
     const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/resend-otp`, {
        method: 'POST',
        body: JSON.stringify({ email: formData.email, forgot_pwd: false }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setStep(2);
      setErrors({});

    } catch (error) {
      setErrors({ email: 'Failed to send OTP. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.otp.trim()) {
      setErrors({ otp: 'OTP is required' });
      return;
    }
    
    if (formData.otp.length !== 4) {
      setErrors({ otp: 'OTP must be 4 digits' });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Simulate API call to verify OTP
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/delete-user`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: formData.email, otp: formData.otp }),
      });
      if (!response.ok) {
        throw new Error(`Failed to delete account. Please try again.`);
      }
      // Move to confirmation step
      setShowConfirmation(true);
    } catch (error) {
      setErrors({ otp: 'Invalid OTP. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccountDeletion = async () => {
    setIsLoading(true);
    
    try {
      // Simulate API call to delete account
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setShowConfirmation(true);
    } catch (error) {
      setErrors({ general: 'Failed to delete account. Please contact support.' });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setFormData({ email: '', otp: '', reason: '' });
    setErrors({});
    setShowConfirmation(false);
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.iconContainer}>
            <Shield className={styles.icon} />
          </div>
          <h1 className={styles.title}>Account Deletion</h1>
          <p className={styles.subtitle}>
            Enter your email to receive verification code
          </p>
        </div>

        {/* Form Content */}
        <div className={styles.content}>
          {step === 1 && (
            <form onSubmit={handleEmailSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.label}>
                  Email Address *
                </label>
                <div className={styles.inputContainer}>
                  <Mail className={styles.inputIcon} />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`${styles.input} ${errors.email ? styles.error : ''}`}
                    placeholder="Enter your email address"
                  />
                </div>
                {errors.email && (
                  <p className={styles.errorMessage}>{errors.email}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={styles.button}
              >
                {isLoading ? (
                  <>
                    <Loader2 className={styles.loadingSpinner} />
                    <span>Sending OTP...</span>
                  </>
                ) : (
                  'Send Verification Code'
                )}
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleOTPSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label htmlFor="otp" className={styles.label}>
                  Verification Code *
                </label>
                <input
                  type="text"
                  id="otp"
                  name="otp"
                  value={formData.otp}
                  onChange={handleInputChange}
                  maxLength={4}
                  className={`${styles.inputOtp} ${errors.otp ? styles.error : ''}`}
                  placeholder="0000"
                />
                {errors.otp && (
                  <p className={styles.errorMessage}>{errors.otp}</p>
                )}
                <div className={styles.infoBox}>
                  <p>
                    We've sent a 6-digit code to <strong>{formData.email}</strong>
                  </p>
                </div>
              </div>

              <div className={styles.buttonGroup}>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className={`${styles.button} ${styles.buttonSecondary}`}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={styles.button}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className={styles.loadingSpinner} />
                      <span>Verifying...</span>
                    </>
                  ) : (
                    'Verify Code'
                  )}
                </button>
              </div>
            </form>
          )}

          {step === 3 && (
            <div className={styles.form}>
              <div className={styles.warningBox}>
                <div className={styles.warningContent}>
                  <div className={styles.warningIcon}>
                    <AlertTriangle />
                  </div>
                  <div className={styles.warningText}>
                    <h3>Warning: This action cannot be undone</h3>
                    <p>
                      Deleting your account will permanently remove all your data, including:
                    </p>
                    <ul>
                      <li>All personal information</li>
                      <li>Account settings and preferences</li>
                      <li>Support ticket history</li>
                      <li>Any associated data</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="reason" className={styles.label}>
                  Reason for deletion (optional)
                </label>
                <textarea
                  id="reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  rows={3}
                  className={styles.textarea}
                  placeholder="Help us improve by telling us why you're leaving..."
                />
              </div>

              <div className={styles.buttonGroup}>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className={`${styles.button} ${styles.buttonSecondary}`}
                >
                  Back
                </button>
                <button
                  onClick={handleAccountDeletion}
                  disabled={isLoading}
                  className={`${styles.button} ${styles.buttonDanger}`}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className={styles.loadingSpinner} />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    'Delete Account'
                  )}
                </button>
              </div>
            </div>
          )}

          {errors.general && (
            <div className={styles.generalError}>
              <p>{errors.general}</p>
            </div>
          )}
        </div>

        {/* Contact Info */}
        <div className={styles.footer}>
          <p>
            Need help? Contact us at{' '}
            <a href={`mailto:${import.meta.env.VITE_SUPPORT_EMAIL}`}>
              {import.meta.env.VITE_SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      </div>

      {/* Success Confirmation Modal */}
      {showConfirmation && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalIcon}>
              <CheckCircle />
            </div>
            <h2 className={styles.modalTitle}>Account Deleted Successfully</h2>
            <p className={styles.modalText}>
              Your account and all associated data have been permanently deleted. 
              You will receive a confirmation email shortly.
            </p>
            <button
              onClick={resetForm}
              className={styles.modalButton}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountDeletion;